import { useState } from 'react';
import { Link } from 'react-router';

import type { ApiError } from '../../api/client';
import { useOnlinePivot } from '../../api/online-pivot';
import { ExportButton } from '../../components/ExportButton';
import { ErrorState } from '../../components/ui';
import { TableWrap } from '../../components/TableWrap';

import {
  cellOf,
  isSummary,
  PIVOT_COLUMNS,
  pivotApplicationsPath,
  pivotCellHint,
  unattributedLabel,
  hasOnlineApplications,
} from './online-pivot-model';
import { pluralRu } from './venues-model';
import './venues.css';

/**
 * «Онлайн-обучение» — the block T.F5 adds above the venues roster.
 *
 * COLLAPSIBLE on purpose: the roster is the page's reason to be, and the block is a report a
 * curator opens when the online backlog is the question. The state is component-local on
 * purpose too — it is a disclosure, not a filter, so it does not belong in the URL (and a
 * reload landing on the roster is the behaviour a manager expects).
 *
 * The first row is the api's synthetic «Онлайн» summary (`venueId: null`) and renders bold;
 * the rest are the venues that actually hold online applications, in the api's name order.
 *
 * T.R6 — the table is the report's way OUT as well: every cell is a link into `/applications`
 * carrying `format=ONLINE`, the row's `venueId` and, on a funnel column, that column's status
 * (`pivotApplicationsPath` owns the mapping). The link fills the cell, so the whole cell is
 * the hit area — cell-level click is the primary interaction and the `Площадки`/`Итого`
 * cells are the row-level fallback (the row scope, every status). A `<tr onClick>` would
 * nest two behaviours in one row and a middle-click / copy-link would lose the target,
 * which is why the row itself stays inert.
 */
export function OnlinePivotBlock() {
  const [open, setOpen] = useState(false);
  const query = useOnlinePivot();
  const rows = query.data?.rows ?? [];
  const populated = hasOnlineApplications(rows);

  return (
    <section className="card card--flush venue-section" data-testid="online-pivot">
      <button
        type="button"
        className="online-pivot__toggle"
        data-testid="online-pivot-toggle"
        aria-expanded={open}
        aria-controls="online-pivot-body"
        onClick={() => setOpen((value) => !value)}
      >
        <span className="online-pivot__title">Онлайн-обучение</span>
        <span className="online-pivot__meta" data-testid="online-pivot-count">
          {query.isPending ? '…' : `${rows[0]?.total ?? 0} заявл.`}
        </span>
        <span className="online-pivot__chevron" aria-hidden="true">
          {open ? '▾' : '▸'}
        </span>
      </button>

      {open ? (
        <div className="online-pivot__body" id="online-pivot-body" data-testid="online-pivot-body">
          {query.isPending ? (
            <p className="online-pivot__empty" data-testid="online-pivot-loading">
              Считаем онлайн-заявления…
            </p>
          ) : query.isError ? (
            <ErrorState
              message={(query.error as ApiError | null)?.message ?? 'Не удалось загрузить онлайн-заявления.'}
              onRetry={() => void query.refetch()}
            />
          ) : !populated ? (
            <p className="online-pivot__empty" data-testid="online-pivot-empty">
              Онлайн-заявлений пока нет.
            </p>
          ) : (
            <>
              {/* T.E1 — the block as a file, table as-is (summary row first). The pivot
                  takes no filter, so the export takes none either; the button lives in
                  the open body because the toggle row is a <button> already. */}
              <div className="online-pivot__actions">
                <ExportButton resource="online-pivot" testId="online-pivot-export" params={{}} label="Экспорт XLSX" />
              </div>
              <TableWrap>
                <table className="data" data-testid="online-pivot-table">
                  <thead>
                    <tr>
                      {PIVOT_COLUMNS.map((column) => (
                        <th key={column.key} className={column.key === 'name' ? undefined : 'num'}>
                          {column.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, index) => (
                      <tr
                        key={row.venueId ?? 'summary'}
                        data-testid="online-pivot-row"
                        data-venue-id={row.venueId ?? 'summary'}
                        className={isSummary(row) ? 'online-pivot__summary' : undefined}
                      >
                        {PIVOT_COLUMNS.map((column) => {
                          // The summary row is the block's first, so its cells keep the
                          // `summary` suffix the venue rows spell with the venue id.
                          const cellId = `pivot-${column.key}-${index === 0 ? 'summary' : row.venueId}`;
                          return (
                            <td
                              key={column.key}
                              className={column.key === 'name' ? 'cell-primary' : 'num'}
                              data-testid={cellId}
                            >
                              <Link
                                className="online-pivot__cell"
                                to={pivotApplicationsPath(row, column.key)}
                                title={pivotCellHint(row, column.key)}
                                data-testid={`${cellId}-link`}
                              >
                                {cellOf(row, column.key)}
                              </Link>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableWrap>
              {(query.data?.unattributed ?? 0) > 0 ? (
                <p className="online-pivot__footnote" data-testid="online-pivot-unattributed">
                  {unattributedLabel(query.data?.unattributed ?? 0, pluralRu)}, они не распределены по площадкам.
                </p>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </section>
  );
}
